import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { selectorAuth, SessionStorageService } from '@app/core';
import { BusinessService } from '../business.service';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { BusinessGroupComponent } from '../business-group/business-group.component';
import { InfoService } from '@app/core/info/info.service';
import { IBusinessUser, ICompany, IService, rolesDictionary, serviceIconCodesDictionary } from '../business-models';
import { IUserData } from '@app/profile/main/details';


@Component({
  selector: 'cloud-business-profile',
  templateUrl: './business-profile.component.html',
  styleUrls: ['./business-profile.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})

export class BusinessProfileComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator, {static: false})
  set paginator(value: MatPaginator) {
    if (this.dataSource){
      this.dataSource.paginator = value;
    }
  }

  private destroy$ = new Subject<void>();

  loading: boolean;
  companyData: ICompany;
  userData: IUserData;
  servicesColumns: string[] = ['icon', 'name'];
  servicesDataSource: IService [] = [];
  dataSource = new MatTableDataSource();
  businessAccountId: string;
  businessLogo: string;


  usersColumns: string[] = ['name', 'role', 'position', 'email','phone','actions'];
  groupsColumns: string[] = ['groupName', 'expand', 'groupActions'];
  isAdmin: boolean = false;
  readonly icons = iconCodes;
  userRole: string;

  private confirmDialog: MatDialogRef<ConfirmComponent>;
  private pageDialog: MatDialogRef<BusinessGroupComponent>;
  asyncInputValue:IBusinessUser [];

  constructor(
    private sessionStorageService: SessionStorageService,
    private businessService: BusinessService,
    public tableHelper: MatTableHelper,
    private store: Store<any>,
    public translateService: TranslateService,
    private dialog: MatDialog,
    public info: InfoService
  ) { }


  ngOnInit() {
    this.loading = true;

    this.store
    .select(selectorAuth)
    .subscribe(auth => {
      if (auth != null) {
        this.userData = auth.userData;
      }
      if(this.userData == undefined){          
        this.userData =  this.sessionStorageService.getItem("loginEventData")      
      }  
    });

    this.getCurrentLoggedInAccount();
    this.getCompanyData();
    this.getUsers();
    this.getGroups();
    this.getAllServices();

    this.updateCompanies(); // Ensures that any role changes made for the current user in the Agent portal are reflected in the Business portal

    /**
     * Subscribing to the updateGroupTable BehaviorSubject to react to group updates
     * */ 
    this.businessService.updateGroupTable.pipe( 
      takeUntil(this.destroy$)
    ).subscribe((update: boolean) => {
      if (update) {
        this.getGroups();
      }
    });

    /**
     * Subscribing to the updateUserTable BehaviorSubject to react to users updates
     * */ 
    this.businessService.updateUserTable.pipe(
      filter(update => update === true), // proceed only when the update value is true (initial emit is false)
      tap(() => this.getUsers()), // update the current users data with updated one (after the user is added/edited) this data will propagate to the child components
      tap(() =>this.updateCompanies()), // update the companies in the session storage when the user changes his role in the company
      takeUntil(this.destroy$)
    ).subscribe();
  }

    ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    }

  getUsers() {
   this.businessService.getUsers(this.businessAccountId).pipe(take(1)).subscribe( res => {
   this.asyncInputValue = res.data;
  });
  }

  getGroups() {
    this.businessService.getCompanyGroups(this.businessAccountId).pipe(take(1)).subscribe( res => {
      this.dataSource = new MatTableDataSource(res.groups);
      this.dataSource.paginator = this.paginator;
    });
  }

  getAllServices() {
    this.businessService.getServices(this.businessAccountId).subscribe( res => {
      this.servicesDataSource = res.data;
    });
  }

  isActive(service) {
    //reports service is active by default for all companies and cannot be disabled
    // this is workaround for old companies that have not have any groups added/removed - then the active services for a company, alongside 'REPORTS', will be rewritten in DB
    if (service.serviceType === 'REPORTS') {
      return true;
    }
    return this.companyData.services.find(s => s.serviceType == service.serviceType);
  }

  getCurrentLoggedInAccount() {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
    const company = this.sessionStorageService.getItem("accounts").find(item => item.cmrId === this.businessAccountId);
    this.userRole = company.role;
    this.isAdmin = this.userRole == 'MY_BTRUST_ADMINISTRATOR' || this.userRole == 'MY_BTRUST_SUPER_ADMINISTRATOR';
  }

  /**
   * This method is used to update the companies in the session storage
   * when the user changes his role in the company
   * */
  updateCompanies() {
  this.businessService.getCompanies(this.userData.cmrId).pipe(take(1)).subscribe(res => {
    if (res.data && res.data.length > 0) {
      this.sessionStorageService.setItem("accounts", res.data); // update the session storage with the new companies (this is needed if the user change his role in the company)
    }
       this.getCurrentLoggedInAccount(); // update the state in the current component
  });
}

  getCompanyData() {
    this.businessService.getCompany(this.businessAccountId).subscribe( res => {
      console.log('getCompanyData',res)
      this.companyData = res.data;
      this.businessLogo = 'data:image/'+ this.getType(this.companyData.logo) +';base64,' + this.companyData.logo;
      this.loading = false;
    })
  }

  getCompanyName() {
      return  this.lang =='bg' ? this.companyData.nameBg : this.companyData.nameEn;
  }

  getServiceName(service) {
    return  this.lang =='bg' ? service.nameBg : service.nameEn;
  }

  getName(e) {
    return  this.lang =='bg' ? e.name : e.nameEn;
  }

  get lang() {
    return this.translateService.currentLang;
  }

  getCurrentUserName() {
    return this.userData?.firstName[this.lang] + " " + this.userData?.lastName[this.lang];
  }

  getCurrentUserRole() {
    return this.translateService.instant('businessTexts.'+ rolesDictionary[this.userRole]);
  }

  getRole(key) {
    return this.translateService.instant('businessTexts.'+ rolesDictionary[key]);
  }
  getIcon(key) {
    return serviceIconCodesDictionary[key];
  }

  getType(imageBase64) { 
    let extension = undefined; 
    const decodedString = window.atob(imageBase64);
    const lowerCase = decodedString.toLowerCase();
    if (lowerCase.indexOf("png") !== -1) extension = "png"
    else if (lowerCase.indexOf("svg") !== -1) extension = "svg+xml"
    else if (lowerCase.indexOf("jpg") !== -1 || lowerCase.indexOf("jpeg") !== -1)
        extension = "jpg"
    return extension;
}

addGroup() {
  this.openGroupDialog('add', this.companyData.services);
}

onEditGroup(group){
  this.openGroupDialog('edit',this.companyData.services, group);
}

onDeleteGroup(group){
  this.openDeleteGroupConfirmDialog(group);
}


openDeleteGroupConfirmDialog(group) {
  this.confirmDialog = this.dialog.open(
    ConfirmComponent,
    {
      data: {
        title: this.translateService.instant('businessTexts.dialogTexts.deleteGroupTitle'),
        question: this.translateService.instant('businessTexts.dialogTexts.deleteGroupQuestion', { group: this.getName(group) }),
        confirmTxt: this.translateService.instant('settingsList.common.confirm'),
        cancelTxt: this.translateService.instant('settingsList.common.cancel'),
        confirmAction: () => this.deleteGroupFunction(group)
      } as IConfirmData
    });
}

deleteGroupFunction(group) {
  this.businessService.deleteGroup(group.id).subscribe(res => {
    if (res.status == 200) {
      this.info.success(this.translateService.instant('businessTexts.successDeleteGroup'));
      this.businessService.updateUserTable.next(true);
      this.businessService.updateGroupTable.next(true);
      this.businessService.updateNavBar.next(true);
    } else {
      this.info.error(this.translateService.instant('businessTexts.errorDeleteGroup'));
    }
  })
  this.confirmDialog.close();
}

openGroupDialog(action, companyServices, group?) {
  const dialogConfig = new MatDialogConfig();
  dialogConfig.disableClose = false;
  dialogConfig.autoFocus = true;
  dialogConfig.data = {
      action: action,
      services: companyServices
  };
  dialogConfig.panelClass = 'panel-class';

  if(group) {
    dialogConfig.data = {
      action: action,
      group: group,
      services: companyServices
  };
  }
  this.pageDialog = this.dialog.open(BusinessGroupComponent, dialogConfig);


  const sub = this.pageDialog.componentInstance.close.subscribe((close: boolean) => {
    this.pageDialog.close();
});
}  

}
